import axios from 'axios';
import { keystorePut } from '../../webservice/keyStore';

// the zE function is added to the global scope by the Zendesk Web Widget snippet
declare function zE(domain: string, command: string, arg?: any, arg2?: any): Promise<unknown>;
// We additionally add our own data to it to keep track of which field to send TripChange
// data to
declare namespace zE {
  let tripChangeFieldID: string;
}

type ConversationOptions = {
  displayName: string;
  iconUrl: string;
  metadata: object;
  message: {
    content: {
      type: 'text',
      text: string,
    }
  }
}

type ConversationField = {
  id: string;
  value: string | number | boolean;
};

async function getJWTToken() {
  try {
    const response = await axios.post('/api/v1/zendeskchat/jwt', {});
    return response.data;
  } catch (err) {
    throw err.response ? err.response : err;
  }
}

export function openZendeskChat() {
  zE('messenger', 'open');
}

export function closeZendeskChat() {
  zE('messenger', 'close');
}

export function showZendeskChat() {
  zE('messenger', 'show');
}

export function hideZendeskChat() {
  zE('messenger', 'hide');
}

export function setCustomization(themeData: object) {
  zE('messenger:set', 'customization', themeData);
}

export function loginZendesk() {
  return new Promise((res, rej) => {
    zE(
      'messenger',
      'loginUser',
      (cb: (token: string) => void) => {
        getJWTToken()
          .then(({ token }: { token: string }) => {
            cb(token);
          })
          .catch((error) => {
            console.error('Failed to fetch new JWT:', error);
            // Optional: Log the user out or notify them
          });
      },
      (err: string) => {
        if (err) {
          rej(err);
        } else {
          res(true);
        }
      },
    );
  });
}

export function newConversation(options?: ConversationOptions) {
  return zE('messenger:ui', 'newConversation', options);
}

export function setZendeskConversationFields(fields: ConversationField[]) {
  return zE('messenger:set', 'conversationFields', fields);
}

export function resetZendeskChat() {
  return new Promise((res) => {
    zE('messenger', 'resetWidget', res);
  });
}

export function connectZendeskChat(themeData: object) {
  window.startProactiveChat = async () => {
    setCustomization(themeData)
    await loginZendesk();
    openZendeskChat();
    showZendeskChat();
  };
  window.startChatWithMessage = async (msg: string) => {
    // doesnt work The zendesk widget wont let us inject a message
    // just open for now
    setCustomization(themeData)
    await loginZendesk();
    openZendeskChat();
    showZendeskChat();
  };

  // startModifyChat is used by the Trip Change center to begin chats about a specific change
  // request
  window.startModifyChat = async (msg: string, data: any) => {
    // Stash our modify trips request data in our own API for retrieval later using the returned key
    const key = await keystorePut(JSON.stringify(data));

    // This kind of chat is always a brand new conversation, so reset everything
    await resetZendeskChat();
    setCustomization(themeData)
    await loginZendesk();

    // Set the custom field on the support ticket we are about to create to our stashed data's key
    await setZendeskConversationFields([{ id: zE.tripChangeFieldID, value: key }]);

    // Automatically start a new conversation about this particular change request
    newConversation();

    openZendeskChat();
    showZendeskChat();
  };
}

export function connectLoggedOutZendesk(themeData: object) {
  window.startProactiveChat = async () => {
    setCustomization(themeData);
    openZendeskChat();
    showZendeskChat();
  }
  return window.startProactiveChat;
}